import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import ResponsiveModal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import BookingForm from "@shared/cards/bookingForm";
import BookingSummaryCard from "@shared/cards/ookingSummary";
import BookingConfirmation from "@shared/cards/bookingConfirmed";
import { BaseLayout } from "@layouts/BaseLayout";
import { DashboardLayout } from "@layouts/DashboardLayout";
import {
  getProfile,
  initiatePayment,
  initiateBankTransfer,
  uploadBankTransferProof,
  getSingleProperty,
  checkPropertyAvailability,
  resumePayment,
  cancelTransaction,
} from "@services/krent.service";
import { validateCurrentToken, clearAuthData } from "@utils/tokenUtils";
import { toCalendarDateString } from "@utils/helpers";
import { Modal } from "@shared/controls/Modal";
import DatePicker from "react-datepicker";
import { addDays } from "date-fns";
import SecuringBooking from "@shared/cards/booking-loading";
import "react-datepicker/dist/react-datepicker.css";
import { toast, ToastContainer } from "react-toastify";

interface BookingData {
  propertyImage?: string;
  propertyName?: string;
  propertyRef?: string;
  propertyId?: string;
  propertySlug?: string;
  rating?: number;
  reviewCount?: number;
  dateFrom?: Date | null;
  dateTo?: Date | null;
  guests?: {
    adults: number;
    children: number;
    infants: number;
    pets: number;
  };
  pricePerNight?: number;
  numberOfDays?: number;
  cautionFee?: number;
  serviceFee?: number;
  currency?: string;
  intent?: string;
  usePartyPrice?: boolean;
  /** When false, skip the guest profile form (phone + WhatsApp already on profile) */
  showGuestProfile?: boolean;
  slug?: string;
}

const Booking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>({});
  // Extract slug from URL path
  const pathParts = location.pathname.split("/");
  const slugFromPath = pathParts[2]; // Assuming URL is /property/{slug}/book
  console.log("Slug from path:", slugFromPath);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editMode, setEditMode] = useState<"dates" | "guests" | null>(null);
  const [editableBookingData, setEditableBookingData] = useState<BookingData>(
    {},
  );
  const [isFormValid, setIsFormValid] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(() => {
    const state = location.state as BookingData | null;
    if (state && typeof state.showGuestProfile === "boolean") {
      return state.showGuestProfile;
    }
    try {
      const pending = sessionStorage.getItem("pendingBooking");
      if (pending) {
        const parsed = JSON.parse(pending);
        if (typeof parsed?.showGuestProfile === "boolean") {
          return parsed.showGuestProfile;
        }
      }
    } catch {
      // ignore
    }
    return true;
  });
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleTermsChange = (accepted: boolean) => {
    console.log("Booking page - handleTermsChange called with:", accepted);
    setTermsAccepted(accepted);
  };
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isProcessingTransfer, setIsProcessingTransfer] = useState(false);
  const [showBookingLoading, setShowBookingLoading] = useState(false);
  const [showBookingConfirmed, setShowBookingConfirmed] = useState(false);
  const [paymentReference, setPaymentReference] = useState<string>("");
  const [guestFormData, setGuestFormData] = useState<any>({});
  const [datesAvailable, setDatesAvailable] = useState<boolean | null>(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState<string | null>(
    null,
  );
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeModalLoading, setResumeModalLoading] = useState<
    "resume" | "cancel" | null
  >(null);
  const [resumableSession, setResumableSession] = useState<{
    transactionId: string;
    message: string;
    paymentMethod?: "paystack" | "manual_bank_transfer";
    bankDetails?: {
      accountNumber: string;
      bankName: string;
      accountName: string;
    };
    pendingReceipt?: File;
    pendingRemarks?: string;
  } | null>(null);
  const [transferBankDetails, setTransferBankDetails] = useState<{
    accountNumber: string;
    bankName: string;
    accountName: string;
  } | null>(null);
  // Get booking data from location state or sessionStorage
  const bookingData: BookingData = useMemo(() => {
    console.log("Booking page - Getting booking data");
    console.log("Location state:", location.state);

    if (
      location.state &&
      typeof location.state === "object" &&
      Object.keys(location.state).length > 0
    ) {
      console.log("Using location state as booking data");
      return location.state as BookingData;
    }

    const pendingBooking = sessionStorage.getItem("pendingBooking");
    console.log("Pending booking from sessionStorage:", pendingBooking);

    if (pendingBooking) {
      try {
        const parsed = JSON.parse(pendingBooking);
        console.log("Parsed booking data:", parsed);
        return parsed;
      } catch (e) {
        console.error("Error parsing pending booking:", e);
        return {};
      }
    }

    console.log("No booking data found");
    return {};
  }, [location.state]);

  useEffect(() => {
    // Convert string dates to Date objects for DatePicker
    const sanitizedBookingData = {
      ...bookingData,
      dateFrom: bookingData.dateFrom
        ? typeof bookingData.dateFrom === "string"
          ? new Date(bookingData.dateFrom)
          : bookingData.dateFrom
        : null,
      dateTo: bookingData.dateTo
        ? typeof bookingData.dateTo === "string"
          ? new Date(bookingData.dateTo)
          : bookingData.dateTo
        : null,
    };
    setEditableBookingData(sanitizedBookingData);

    // Clear sessionStorage data after loading if it was used
    if (
      !location.state ||
      (typeof location.state === "object" &&
        Object.keys(location.state).length === 0)
    ) {
      sessionStorage.removeItem("pendingBooking");
    }
  }, [bookingData, location.state]);

  // Fetch property details to get maxGuests
  useEffect(() => {
    const abort = new AbortController();
    const fetchPropertyDetails = async () => {
      if (slugFromPath) {
        try {
          const response: any = await getSingleProperty(
            slugFromPath,
            abort.signal,
          );

          if (response?.data) {
            const propertyData = response.data;
            setEditableBookingData((prev) => ({
              ...prev,
              maxGuests: propertyData.guests || 10,
              maxPets: propertyData.pets || 0,
            }));
          }
        } catch (error) {
          console.error("Failed to fetch property details:", error);
        }
      }
    };

    fetchPropertyDetails();
    return () => abort.abort();
  }, [slugFromPath]);

  useEffect(() => {
    const abort = new AbortController();
    if (validateCurrentToken()) {
      getProfile(abort.signal)
        .then((re: any) => {
          setUser(re["data"]);
          sessionStorage.setItem("user", JSON.stringify(re["data"]));
          console.log("User profile fetched successfully:", re["data"]);
        })
        .catch((error) => {
          console.error("Failed to fetch user profile:", error);
          clearAuthData();
        });
    } else {
      console.log("No valid token found, skipping user profile fetch");
    }
    return () => abort.abort();
  }, []);

  // Non-renter roles (agents, landlords, etc.) cannot make bookings
  useEffect(() => {
    if (user?.role && user.role !== "user") {
      navigate("/", { replace: true });
    }
  }, [user?.role, navigate]);

  useEffect(() => {
    const propertyId = editableBookingData.propertyId;
    const { dateFrom, dateTo } = editableBookingData;

    if (!propertyId || !dateFrom || !dateTo) {
      setDatesAvailable(null);
      setAvailabilityMessage(null);
      return;
    }

    const abort = new AbortController();
    setCheckingAvailability(true);

    const arrival = toCalendarDateString(dateFrom);
    const departure = toCalendarDateString(dateTo);

    checkPropertyAvailability(propertyId, arrival, departure, abort.signal)
      .then((res) => {
        if (res.isAvailable) {
          setDatesAvailable(true);
          setAvailabilityMessage(null);
        } else {
          setDatesAvailable(false);
          setAvailabilityMessage(
            "These dates aren't available. Update your dates to continue.",
          );
        }
      })
      .catch((err) => {
        if (err?.name !== "AbortError") {
          setDatesAvailable(null);
          setAvailabilityMessage(null);
        }
      })
      .finally(() => {
        if (!abort.signal.aborted) {
          setCheckingAvailability(false);
        }
      });

    return () => abort.abort();
  }, [
    editableBookingData.propertyId,
    editableBookingData.dateFrom,
    editableBookingData.dateTo,
  ]);

  const handlePayNow = async () => {
    const token =
      sessionStorage.getItem("1Q_SPA") ||
      document.cookie
        .split("; ")
        .find((row) => row.startsWith("1Q_SPA="))
        ?.split("=")[1];
    const user = sessionStorage.getItem("user");

    if (!token || !user) {
      sessionStorage.setItem(
        "pendingBooking",
        JSON.stringify(editableBookingData),
      );
      toast.error("Please sign in or sign up to continue with payment.");
      return;
    }

    if (!guestFormData || Object.keys(guestFormData).length === 0) {
      toast.error("Please fill in your details first");
      return;
    }

    if (datesAvailable === false) {
      toast.error(
        availabilityMessage ||
          "These dates aren't available. Update your dates to continue.",
      );
      return;
    }

    setIsProcessingPayment(true);

    try {
      const arrivalDate = toCalendarDateString(editableBookingData.dateFrom!);
      const departureDate = toCalendarDateString(editableBookingData.dateTo!);

      if (process.env.NODE_ENV === "development") {
        const displayedFrom = toCalendarDateString(
          editableBookingData.dateFrom!,
        );
        const displayedTo = toCalendarDateString(editableBookingData.dateTo!);
        if (displayedFrom !== arrivalDate || displayedTo !== departureDate) {
          console.error("Date mismatch between display and payment payload", {
            displayedFrom,
            displayedTo,
            arrivalDate,
            departureDate,
          });
        }
      }

      const propertyId =
        editableBookingData.propertyId || editableBookingData.propertyRef || "";

      const availabilityCheck = await checkPropertyAvailability(
        propertyId,
        arrivalDate,
        departureDate,
      );
      if (!availabilityCheck.isAvailable) {
        setDatesAvailable(false);
        setAvailabilityMessage(
          "These dates aren't available. Update your dates to continue.",
        );
        toast.error(
          "These dates aren't available. Update your dates to continue.",
        );
        return;
      }

      const paymentPayload = {
        listing: propertyId,
        arrival_date: arrivalDate,
        departure_date: departureDate,
        guest: {
          adult: editableBookingData.guests?.adults || 1,
          children: editableBookingData.guests?.children || 0,
          infants: editableBookingData.guests?.infants || 0,
          pets: editableBookingData.guests?.pets || 0,
        },
        whatsappNumber: guestFormData.whatsapp || guestFormData.phone,
        tc: guestFormData.termsAccepted || true,
        firstname: guestFormData.firstName || guestFormData.firstname,
        lastname: guestFormData.lastName || guestFormData.lastname,
        phone: guestFormData.phone,
        specialRequests: guestFormData.specialRequests || "",
        usePartyPrice: editableBookingData.usePartyPrice || false,
      };

      const paymentResponse = await initiatePayment(paymentPayload);
      console.log(paymentResponse, "paymentResponse");

      // Check if response contains errors even if HTTP status is 200
      if (
        (paymentResponse as any)?.data?.errors &&
        Array.isArray((paymentResponse as any).data.errors)
      ) {
        throw {
          response: {
            data: (paymentResponse as any).data,
          },
        };
      }

      // Check if the response indicates success
      if (
        !(paymentResponse as any)?.success &&
        (paymentResponse as any)?.success !== true
      ) {
        throw new Error("Payment initiation failed");
      }

      // Existing unfinished payment session — ask to resume or cancel
      if ((paymentResponse as any)?.resumable) {
        const transactionId =
          (paymentResponse as any)?.data?.transactionId ||
          (paymentResponse as any)?.data?.id;

        if (!transactionId) {
          throw new Error(
            "Could not find the existing booking session to resume.",
          );
        }

        setResumableSession({
          transactionId,
          message:
            (paymentResponse as any)?.message ||
            "You already have an active booking session for this property. Do you want to resume payment or cancel it?",
        });
        setShowResumeModal(true);
        return;
      }

      const reference = (paymentResponse as any)?.data?.reference;
      const authorizationUrl = (paymentResponse as any)?.data
        ?.authorization_url;

      if (reference) {
        setPaymentReference(reference);
      }

      sessionStorage.setItem(
        "bookingData",
        JSON.stringify(editableBookingData),
      );

      // Show confirming screen for 3 seconds before redirecting
      setShowBookingLoading(true);

      setTimeout(() => {
        if (authorizationUrl) {
          // Redirect to Paystack payment page after 3 seconds
          window.location.href = authorizationUrl;
        } else {
          // Fallback: Show confirmation screen if no authorization URL
          setShowBookingLoading(false);
          setShowBookingConfirmed(true);
          toast.success("Payment initiated successfully!");
        }
      }, 3000);
    } catch (error: any) {
      setShowBookingLoading(false);
      let errorMessage = "Failed to process payment";

      if (
        error.response?.data?.errors &&
        Array.isArray(error.response.data.errors)
      ) {
        errorMessage = error.response.data.errors[0]?.message || errorMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.errors && Array.isArray(error.errors)) {
        errorMessage = error.errors[0]?.message || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      console.error("Payment initiation error:", error);
      toast.error(errorMessage);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const buildBookingPayload = () => {
    const arrivalDate = toCalendarDateString(editableBookingData.dateFrom!);
    const departureDate = toCalendarDateString(editableBookingData.dateTo!);
    const propertyId =
      editableBookingData.propertyId || editableBookingData.propertyRef || "";

    return {
      listing: propertyId,
      arrival_date: arrivalDate,
      departure_date: departureDate,
      guest: {
        adult: editableBookingData.guests?.adults || 1,
        children: editableBookingData.guests?.children || 0,
        infants: editableBookingData.guests?.infants || 0,
        pets: editableBookingData.guests?.pets || 0,
      },
      whatsappNumber: guestFormData.whatsapp || guestFormData.phone,
      tc: guestFormData.termsAccepted || termsAccepted || true,
      firstname: guestFormData.firstName || guestFormData.firstname,
      lastname: guestFormData.lastName || guestFormData.lastname,
      phone: guestFormData.phone,
      specialRequests: guestFormData.specialRequests || "",
      usePartyPrice: editableBookingData.usePartyPrice || false,
    };
  };

  const uploadTransferProof = async (
    transactionId: string,
    paymentProof: File,
    remarks: string,
    referenceId?: string,
  ) => {
    const formData = new FormData();
    formData.append("paymentProof", paymentProof);
    formData.append(
      "paymentProofRenterComment",
      remarks?.trim() || "Transferred via Bank",
    );

    const uploadResponse: any = await uploadBankTransferProof(
      transactionId,
      formData,
    );

    if (uploadResponse?.errors?.length) {
      throw {
        errors: uploadResponse.errors,
        message: uploadResponse.errors[0]?.message,
      };
    }

    if (!uploadResponse?.success && uploadResponse?.success !== true) {
      throw new Error(
        uploadResponse?.message || "Failed to upload payment receipt",
      );
    }

    const uploadData = uploadResponse?.data || {};
    const reference =
      uploadData?.referenceId || uploadData?.reference || referenceId;

    if (reference) {
      setPaymentReference(reference);
    }

    const confirmedBookingData = {
      ...editableBookingData,
      id: uploadData?.id || transactionId,
      status: uploadData?.status || "paid_pending_confirmation",
      paymentMethod: uploadData?.paymentMethod || "manual_bank_transfer",
      adminProofVerified: uploadData?.adminProofVerified ?? false,
      hostAcceptedBankTransfer: uploadData?.hostAcceptedBankTransfer ?? false,
      bookingFlow: uploadData?.bookingFlow,
      sessionExpiresAt: uploadData?.sessionExpiresAt,
      referenceId: reference,
      paymentProof: uploadData?.paymentProof,
      paymentProofRenterComment: uploadData?.paymentProofRenterComment,
      propertyName:
        uploadData?.listing?.title || editableBookingData.propertyName,
      propertyImage:
        editableBookingData.propertyImage || uploadData?.listing?.coverImage,
      currency:
        uploadData?.listing?.currency || editableBookingData.currency || "₦",
      pricePerNight:
        uploadData?.bill?.[0]?.propertyPrice ??
        editableBookingData.pricePerNight,
      cautionFee:
        uploadData?.bill?.[0]?.cautionFee ?? editableBookingData.cautionFee,
      serviceFee:
        uploadData?.bill?.[0]?.serviceCharge ?? editableBookingData.serviceFee,
      instantBooking: uploadData?.listing?.instantBooking ?? false,
    };

    sessionStorage.setItem("bookingData", JSON.stringify(confirmedBookingData));

    toast.success(
      uploadResponse?.message ||
        "Receipt uploaded. Your booking will be confirmed once payment is verified.",
    );

    if (!reference) {
      throw new Error("Missing booking reference after receipt upload.");
    }

    navigate(
      `/booking/confirm?trxref=${encodeURIComponent(reference)}&reference=${encodeURIComponent(reference)}`,
      { replace: true },
    );
  };

  const handlePayWithTransfer = async (receipt: File, remarks: string) => {
    const token =
      sessionStorage.getItem("1Q_SPA") ||
      document.cookie
        .split("; ")
        .find((row) => row.startsWith("1Q_SPA="))
        ?.split("=")[1];
    const user = sessionStorage.getItem("user");

    if (!token || !user) {
      sessionStorage.setItem(
        "pendingBooking",
        JSON.stringify(editableBookingData),
      );
      toast.error("Please sign in or sign up to continue with payment.");
      return;
    }

    if (!guestFormData || Object.keys(guestFormData).length === 0) {
      toast.error("Please fill in your details first");
      return;
    }

    if (datesAvailable === false) {
      toast.error(
        availabilityMessage ||
          "These dates aren't available. Update your dates to continue.",
      );
      return;
    }

    if (!receipt) {
      toast.error("Please upload your payment receipt");
      return;
    }

    setIsProcessingTransfer(true);

    try {
      const propertyId =
        editableBookingData.propertyId || editableBookingData.propertyRef || "";
      const arrivalDate = toCalendarDateString(editableBookingData.dateFrom!);
      const departureDate = toCalendarDateString(editableBookingData.dateTo!);

      const availabilityCheck = await checkPropertyAvailability(
        propertyId,
        arrivalDate,
        departureDate,
      );
      if (!availabilityCheck.isAvailable) {
        setDatesAvailable(false);
        setAvailabilityMessage(
          "These dates aren't available. Update your dates to continue.",
        );
        toast.error(
          "These dates aren't available. Update your dates to continue.",
        );
        return;
      }

      const transferPayload = buildBookingPayload();
      const initiateResponse: any = await initiateBankTransfer(transferPayload);

      if (
        initiateResponse?.data?.errors &&
        Array.isArray(initiateResponse.data.errors)
      ) {
        throw { response: { data: initiateResponse.data } };
      }

      if (!initiateResponse?.success && initiateResponse?.success !== true) {
        throw new Error(
          initiateResponse?.message || "Failed to initiate bank transfer",
        );
      }

      const responseBankDetails = initiateResponse?.data?.bankDetails;
      if (responseBankDetails) {
        setTransferBankDetails({
          accountNumber: responseBankDetails.accountNumber || "",
          bankName: responseBankDetails.bankName || "",
          accountName: responseBankDetails.accountName || "",
        });
      }

      // Existing unfinished bank transfer session
      if (initiateResponse?.resumable) {
        const transactionId = initiateResponse?.data?.transactionId;
        if (!transactionId) {
          throw new Error(
            "Could not find the existing booking session to resume.",
          );
        }

        setResumableSession({
          transactionId,
          message:
            initiateResponse?.message ||
            "You already have an active booking session for this property. Do you want to proceed or cancel it?",
          paymentMethod: "manual_bank_transfer",
          bankDetails: responseBankDetails,
          pendingReceipt: receipt,
          pendingRemarks: remarks,
        });
        setShowResumeModal(true);
        return;
      }

      const transaction = initiateResponse?.data?.transaction;
      const transactionId = transaction?.id;

      if (!transactionId) {
        throw new Error("Could not start bank transfer booking.");
      }

      await uploadTransferProof(
        transactionId,
        receipt,
        remarks,
        transaction?.referenceId,
      );
    } catch (error: any) {
      let errorMessage = "Failed to submit bank transfer payment";

      if (
        error.response?.data?.errors &&
        Array.isArray(error.response.data.errors)
      ) {
        errorMessage = error.response.data.errors[0]?.message || errorMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.errors && Array.isArray(error.errors)) {
        errorMessage = error.errors[0]?.message || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      console.error("Bank transfer payment error:", error);
      toast.error(errorMessage);
    } finally {
      setIsProcessingTransfer(false);
    }
  };

  const handleResumeExistingSession = async () => {
    if (!resumableSession?.transactionId) return;

    setResumeModalLoading("resume");
    try {
      if (resumableSession.paymentMethod === "manual_bank_transfer") {
        if (!resumableSession.pendingReceipt) {
          toast.error("Please upload your payment receipt to proceed.");
          setShowResumeModal(false);
          return;
        }

        await uploadTransferProof(
          resumableSession.transactionId,
          resumableSession.pendingReceipt,
          resumableSession.pendingRemarks || "",
        );
        setShowResumeModal(false);
        setResumableSession(null);
        return;
      }

      const response: any = await resumePayment(resumableSession.transactionId);

      if (response?.errors?.length) {
        toast.error(response.errors[0]?.message || "Failed to resume payment");
        return;
      }

      const authorizationUrl =
        response?.data?.authorization_url || response?.authorization_url;
      const reference = response?.data?.reference || response?.reference;

      if (reference) {
        setPaymentReference(reference);
      }

      sessionStorage.setItem(
        "bookingData",
        JSON.stringify(editableBookingData),
      );

      if (authorizationUrl) {
        setShowResumeModal(false);
        setShowBookingLoading(true);
        window.location.href = authorizationUrl;
        return;
      }

      toast.error("No payment link returned. Please try again.");
    } catch (error: any) {
      toast.error(
        error?.errors?.[0]?.message ||
          error?.message ||
          "Failed to resume payment",
      );
    } finally {
      setResumeModalLoading(null);
    }
  };

  const handleCancelExistingSession = async () => {
    if (!resumableSession?.transactionId) return;

    setResumeModalLoading("cancel");
    try {
      const response: any = await cancelTransaction(
        resumableSession.transactionId,
        "Cancelled unfinished payment session",
      );

      if (response?.errors?.length) {
        toast.error(
          response.errors[0]?.message || "Failed to cancel transaction",
        );
        return;
      }

      const isBankTransfer =
        resumableSession.paymentMethod === "manual_bank_transfer";

      toast.success(
        isBankTransfer
          ? "Previous bank transfer session cancelled. You can start a new transfer."
          : "Previous booking session cancelled. You can pay again now.",
      );
      setShowResumeModal(false);
      setResumableSession(null);

      if (!isBankTransfer) {
        navigate("/my-bookings");
      }
    } catch (error: any) {
      toast.error(
        error?.errors?.[0]?.message ||
          error?.message ||
          "Failed to cancel transaction",
      );
    } finally {
      setResumeModalLoading(null);
    }
  };

  const handleValidationChange = (isValid: boolean) => {
    setIsFormValid(isValid);
  };

  const handleGuestDataChange = (formData: any) => {
    setGuestFormData(formData);
  };

  const handleSaveChanges = () => {
    setEditableBookingData({ ...editableBookingData });
    setShowEditModal(false);
    setEditMode(null);
  };

  const handleCancelEdit = () => {
    setEditableBookingData(bookingData);
    setShowEditModal(false);
    setEditMode(null);
  };

  const bookingContent = (
    <div className="container py-3">
      <div className="mb-2">
        <button
          onClick={() => navigate(-1)}
          className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2 font-onest"
          style={{ color: "#495057" }}
        >
          <i className="fas fa-arrow-left"></i>
          <span className="font-onest">Back to property</span>
        </button>
      </div>

      <div className="row g-3">
        <div className={showBookingForm ? "col-lg-8" : "col-12"}>
          <BookingForm
            initialShowForm={bookingData.showGuestProfile}
            onValidationChange={handleValidationChange}
            onGuestDataChange={handleGuestDataChange}
            onVisibilityChange={setShowBookingForm}
            onTermsChange={handleTermsChange}
          />
        </div>

        <div className={showBookingForm ? "col-lg-4 mt-4" : ""}>
          <div className="position-sticky" style={{ top: "20px" }}>
            <BookingSummaryCard
              propertyImage={editableBookingData.propertyImage || ""}
              propertyName={editableBookingData.propertyName || ""}
              propertyRef={editableBookingData.propertyRef || ""}
              rating={editableBookingData.rating || 0}
              reviewCount={editableBookingData.reviewCount || 0}
              dateFrom={editableBookingData.dateFrom || null}
              dateTo={editableBookingData.dateTo || null}
              onChangeDates={(dateFrom, dateTo) => {
                setEditableBookingData((prev) => ({
                  ...prev,
                  dateFrom,
                  dateTo,
                  numberOfDays:
                    dateFrom && dateTo
                      ? Math.ceil(
                          (dateTo.getTime() - dateFrom.getTime()) /
                            (1000 * 60 * 60 * 24),
                        )
                      : 0,
                }));
              }}
              guests={
                editableBookingData.guests || {
                  adults: 1,
                  children: 0,
                  infants: 0,
                  pets: 0,
                }
              }
              onChangeGuests={(guests) => {
                setEditableBookingData((prev) => ({ ...prev, guests }));
              }}
              propertyId={editableBookingData.propertyId || ""}
              maxGuests={(editableBookingData as any).maxGuests || 10}
              maxPets={(editableBookingData as any).maxPets || 0}
              normalPrice={editableBookingData.pricePerNight || 0}
              partyPrice={(editableBookingData as any).partyPrice}
              isPartyPrice={(editableBookingData as any).isPartyPrice || false}
              propertyType={
                (editableBookingData as any).propertyType || "shortlet"
              }
              pricePerNight={editableBookingData.pricePerNight || 0}
              numberOfDays={editableBookingData.numberOfDays || 0}
              cautionFee={editableBookingData.cautionFee || 0}
              serviceFee={editableBookingData.serviceFee || 0}
              currency={editableBookingData.currency || "₦"}
              onPayNow={handlePayNow}
              onPayWithTransfer={handlePayWithTransfer}
              payNowDisabled={
                (showBookingForm ? !isFormValid : !termsAccepted) ||
                isProcessingPayment ||
                isProcessingTransfer ||
                checkingAvailability ||
                datesAvailable === false
              }
              loading={isProcessingPayment || checkingAvailability}
              transferLoading={isProcessingTransfer}
              bankDetails={transferBankDetails}
              availabilityMessage={availabilityMessage}
              showTermsCheckbox={!showBookingForm}
              termsAccepted={termsAccepted}
              onTermsChange={handleTermsChange}
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (showBookingLoading) {
    return <SecuringBooking />;
  }

  if (showBookingConfirmed) {
    return (
      <BookingConfirmation
        bookingData={editableBookingData}
        paymentReference={paymentReference}
      />
    );
  }

  return (
    <>
      <ToastContainer limit={1} />

      {user && user?.id ? (
        <DashboardLayout>{bookingContent}</DashboardLayout>
      ) : (
        <BaseLayout>{bookingContent}</BaseLayout>
      )}

      <ResponsiveModal
        center
        open={showResumeModal}
        onClose={() => {
          if (!resumeModalLoading) {
            setShowResumeModal(false);
          }
        }}
        showCloseIcon={false}
        animationDuration={200}
        classNames={{
          overlay: "disclaimer-overlay",
          modal: "disclaimer-modal",
        }}
      >
        <div className="disclaimer-content">
          <div className="disclaimer-icon">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
          </div>
          <h6 className="disclaimer-title">
            {resumableSession?.paymentMethod === "manual_bank_transfer"
              ? "Resume bank transfer?"
              : "Resume payment?"}
          </h6>
          <p className="disclaimer-description">
            {resumableSession?.message ||
              "You already have an active booking session for this property. Do you want to proceed or cancel it?"}
          </p>
          <div className="disclaimer-actions">
            <button
              type="button"
              className="disclaimer-cancel-btn"
              onClick={handleCancelExistingSession}
              disabled={!!resumeModalLoading}
            >
              {resumeModalLoading === "cancel"
                ? "Cancelling..."
                : "Cancel transaction"}
            </button>
            <button
              type="button"
              className="disclaimer-confirm-btn"
              onClick={handleResumeExistingSession}
              disabled={!!resumeModalLoading}
            >
              {resumeModalLoading === "resume"
                ? resumableSession?.paymentMethod === "manual_bank_transfer"
                  ? "Uploading..."
                  : "Resuming..."
                : resumableSession?.paymentMethod === "manual_bank_transfer"
                  ? "Proceed & upload receipt"
                  : "Resume payment"}
            </button>
          </div>
        </div>
      </ResponsiveModal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onClose={handleCancelEdit} size="lg">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title font-onest">
                {editMode === "dates" ? "Change Dates" : "Change Guests"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCancelEdit}
              ></button>
            </div>
            <div className="modal-body">
              {editMode === "dates" && (
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label fw-semibold mb-2 font-onest">
                      Check-in
                    </label>
                    <DatePicker
                      selected={editableBookingData.dateFrom}
                      onChange={(date) =>
                        setEditableBookingData({
                          ...editableBookingData,
                          dateFrom: date,
                        })
                      }
                      className="form-control font-onest"
                      minDate={new Date()}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="Select check-in date"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold mb-2 font-onest">
                      Check-out
                    </label>
                    <DatePicker
                      selected={editableBookingData.dateTo}
                      onChange={(date) =>
                        setEditableBookingData({
                          ...editableBookingData,
                          dateTo: date,
                        })
                      }
                      className="form-control font-onest"
                      minDate={
                        editableBookingData.dateFrom
                          ? addDays(editableBookingData.dateFrom, 1)
                          : null
                      }
                      dateFormat="dd-MM-yyyy"
                      placeholderText="Select check-out date"
                    />
                  </div>
                </div>
              )}

              {editMode === "guests" && (
                <div className="row g-3">
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <div className="fw-semibold font-onest">Adults</div>
                        <div className="text-muted small font-onest">
                          Age 18+
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          className="btn btn-outline-secondary rounded-circle"
                          style={{ width: "36px", height: "36px" }}
                          onClick={() =>
                            setEditableBookingData({
                              ...editableBookingData,
                              guests: {
                                adults: Math.max(
                                  1,
                                  (editableBookingData.guests?.adults || 1) - 1,
                                ),
                                children:
                                  editableBookingData.guests?.children || 0,
                                infants:
                                  editableBookingData.guests?.infants || 0,
                                pets: editableBookingData.guests?.pets || 0,
                              },
                            })
                          }
                        >
                          <i className="fas fa-minus"></i>
                        </button>
                        <span
                          className="fw-semibold font-onest"
                          style={{ minWidth: "30px", textAlign: "center" }}
                        >
                          {editableBookingData.guests?.adults || 1}
                        </span>
                        <button
                          className="btn btn-outline-secondary rounded-circle"
                          style={{ width: "36px", height: "36px" }}
                          onClick={() =>
                            setEditableBookingData({
                              ...editableBookingData,
                              guests: {
                                adults:
                                  (editableBookingData.guests?.adults || 1) + 1,
                                children:
                                  editableBookingData.guests?.children || 0,
                                infants:
                                  editableBookingData.guests?.infants || 0,
                                pets: editableBookingData.guests?.pets || 0,
                              },
                            })
                          }
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <div className="fw-semibold font-onest">Children</div>
                        <div className="text-muted small font-onest">
                          Ages 2-17
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          className="btn btn-outline-secondary rounded-circle"
                          style={{ width: "36px", height: "36px" }}
                          onClick={() =>
                            setEditableBookingData({
                              ...editableBookingData,
                              guests: {
                                adults: editableBookingData.guests?.adults || 1,
                                children: Math.max(
                                  0,
                                  (editableBookingData.guests?.children || 0) -
                                    1,
                                ),
                                infants:
                                  editableBookingData.guests?.infants || 0,
                                pets: editableBookingData.guests?.pets || 0,
                              },
                            })
                          }
                        >
                          <i className="fas fa-minus"></i>
                        </button>
                        <span
                          className="fw-semibold font-onest"
                          style={{ minWidth: "30px", textAlign: "center" }}
                        >
                          {editableBookingData.guests?.children || 0}
                        </span>
                        <button
                          className="btn btn-outline-secondary rounded-circle"
                          style={{ width: "36px", height: "36px" }}
                          onClick={() =>
                            setEditableBookingData({
                              ...editableBookingData,
                              guests: {
                                adults: editableBookingData.guests?.adults || 1,
                                children:
                                  (editableBookingData.guests?.children || 0) +
                                  1,
                                infants:
                                  editableBookingData.guests?.infants || 0,
                                pets: editableBookingData.guests?.pets || 0,
                              },
                            })
                          }
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <div className="fw-semibold font-onest">Infants</div>
                        <div className="text-muted small font-onest">
                          Under 2
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          className="btn btn-outline-secondary rounded-circle"
                          style={{ width: "36px", height: "36px" }}
                          onClick={() =>
                            setEditableBookingData({
                              ...editableBookingData,
                              guests: {
                                adults: editableBookingData.guests?.adults || 1,
                                children:
                                  editableBookingData.guests?.children || 0,
                                infants: Math.max(
                                  0,
                                  (editableBookingData.guests?.infants || 0) -
                                    1,
                                ),
                                pets: editableBookingData.guests?.pets || 0,
                              },
                            })
                          }
                        >
                          <i className="fas fa-minus"></i>
                        </button>
                        <span
                          className="fw-semibold font-onest"
                          style={{ minWidth: "30px", textAlign: "center" }}
                        >
                          {editableBookingData.guests?.infants || 0}
                        </span>
                        <button
                          className="btn btn-outline-secondary rounded-circle"
                          style={{ width: "36px", height: "36px" }}
                          onClick={() =>
                            setEditableBookingData({
                              ...editableBookingData,
                              guests: {
                                adults: editableBookingData.guests?.adults || 1,
                                children:
                                  editableBookingData.guests?.children || 0,
                                infants:
                                  (editableBookingData.guests?.infants || 0) +
                                  1,
                                pets: editableBookingData.guests?.pets || 0,
                              },
                            })
                          }
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-semibold font-onest">Pets</div>
                        <div className="text-muted small font-onest">Max 2</div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          className="btn btn-outline-secondary rounded-circle"
                          style={{ width: "36px", height: "36px" }}
                          onClick={() =>
                            setEditableBookingData({
                              ...editableBookingData,
                              guests: {
                                adults: editableBookingData.guests?.adults || 1,
                                children:
                                  editableBookingData.guests?.children || 0,
                                infants:
                                  editableBookingData.guests?.infants || 0,
                                pets: Math.max(
                                  0,
                                  (editableBookingData.guests?.pets || 0) - 1,
                                ),
                              },
                            })
                          }
                        >
                          <i className="fas fa-minus"></i>
                        </button>
                        <span
                          className="fw-semibold font-onest"
                          style={{ minWidth: "30px", textAlign: "center" }}
                        >
                          {editableBookingData.guests?.pets || 0}
                        </span>
                        <button
                          className="btn btn-outline-secondary rounded-circle"
                          style={{ width: "36px", height: "36px" }}
                          onClick={() =>
                            setEditableBookingData({
                              ...editableBookingData,
                              guests: {
                                adults: editableBookingData.guests?.adults || 1,
                                children:
                                  editableBookingData.guests?.children || 0,
                                infants:
                                  editableBookingData.guests?.infants || 0,
                                pets: Math.min(
                                  2,
                                  (editableBookingData.guests?.pets || 0) + 1,
                                ),
                              },
                            })
                          }
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary font-onest"
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary font-onest"
                onClick={handleSaveChanges}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Booking;
